import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { portfolioApi, type IniciativaConsolidada } from '../../api/portfolio.js';

interface SandboxCenarioProps {
  portfolioId: string;
  iniciativas: IniciativaConsolidada[];
  onPromoverDecisao: (iniciativasCanceladas: string[]) => void;
}

// SPEC-UX §3.8 — modo cenário com moldura visual própria (borda e faixa
// "SIMULAÇÃO — nada será alterado"), paleta dessaturada, único ponto de
// saída: descartar ou "Promover a decisão" (abre a revisão formal). R8.6.
export function SandboxCenario({ portfolioId, iniciativas, onPromoverDecisao }: SandboxCenarioProps) {
  const [ativo, setAtivo] = useState(false);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());

  const simular = useMutation({ mutationFn: (cancelamentos: string[]) => portfolioApi.simular(portfolioId, cancelamentos) });

  function alternar(id: string) {
    setSelecionadas((prev) => {
      const nova = new Set(prev);
      if (nova.has(id)) nova.delete(id);
      else nova.add(id);
      return nova;
    });
  }

  if (!ativo) {
    return (
      <button type="button" onClick={() => setAtivo(true)} className="self-start rounded border border-borda px-3 py-1.5 text-xs font-medium text-grafite hover:bg-borda/40">
        Simular cenário
      </button>
    );
  }

  return (
    <div className="rounded-lg border-2 border-dashed border-grafite bg-borda/10 p-4 saturate-50">
      <div className="mb-3 flex items-center justify-between">
        <span className="fonte-mono rounded bg-grafite/20 px-2 py-0.5 text-xs font-semibold uppercase text-grafite">Simulação — nada será alterado</span>
        <button
          type="button"
          onClick={() => {
            setAtivo(false);
            setSelecionadas(new Set());
            simular.reset();
          }}
          className="text-xs text-grafite hover:text-ink"
        >
          descartar
        </button>
      </div>

      <p className="mb-2 text-xs text-grafite">Selecione iniciativas para simular cancelamento:</p>
      <ul className="flex flex-col gap-1">
        {iniciativas.map((ini) => (
          <li key={`${ini.tipo}_${ini.id}`}>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={selecionadas.has(ini.id)} onChange={() => alternar(ini.id)} />
              {ini.tipo}: {ini.id} <span className="fonte-mono text-xs text-grafite">({ini.estado})</span>
            </label>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => simular.mutate([...selecionadas])}
        disabled={selecionadas.size === 0 || simular.isPending}
        className="mt-3 rounded bg-grafite px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
      >
        Calcular efeito
      </button>

      {simular.data && (
        <div className="mt-3 rounded border border-grafite/30 bg-superficie p-3 text-sm">
          <p className="text-ink">Capacidade liberada: {simular.data.capacidadeLiberadaH.toFixed(0)}h</p>
          <p className="text-xs text-grafite">{simular.data.iniciativasRemovidas.length} iniciativa(s) removida(s) do cenário</p>
          <button
            type="button"
            onClick={() => onPromoverDecisao(simular.data.iniciativasRemovidas)}
            className="mt-2 rounded bg-aco px-3 py-1.5 text-xs font-medium text-white hover:bg-aco-700"
          >
            Promover a decisão
          </button>
        </div>
      )}
    </div>
  );
}
